import { UserRoleEnum } from '@/modules/core/models/enums'

import CommonMapper from '@/modules/core/mappers/CommonMapper'
import Insurance from '@/modules/common/common/models/APP/Insurance'
import UserDetails from '@/modules/common/hiring/models/APP/UserDetails'
import DocumentDetails from '@/modules/common/hiring/models/APP/DocumentDetails'
import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
import BankDetails from '@/modules/common/hiring/models/APP/BankDetails'
import ProcessStatus from '@/modules/common/hiring/models/APP/ProcessStatus'
import DentalHiringAnswers from '@/modules/dental/hiring/models/APP/DentalHiringAnswers'
import DentalHiringInsured from '@/modules/dental/hiring/models/APP/DentalHiringInsured'
import type DentalGetRecoveryResponse from '@/modules/dental/hiring/models/MID/responses/DentalGetRecoveryResponse'
import type DentalInsurance from '@/modules/dental/common/models/APP/DentalInsurance'
import type DentalRelationshipType from '@/modules/dental/common/models/DXP/DentalRelationshipType'
import { EXTENSION_MIMETYPE } from '@/modules/common/common/constants/CommonConstants'

function _getHealthInsured(data: any, role: UserRoleEnum, file?: any) {
  const mimeType = file?.mimeType
    ? EXTENSION_MIMETYPE[file.mimeType.toLowerCase()]
    : 'application/pdf'

  const dataProtection = data?.dataProtection

  return new DentalHiringInsured({
    riskNumber: data?.riskNumber,
    role: role,
    isFirstInsured: data?.isFirstInsured,
    userDetails: new UserDetails({
      birthDate: data?.birthDate,
      name: data?.name?.toUpperCase(),
      surname1: data?.firstSurname?.toUpperCase(),
      surname2: data?.secondSurname?.toUpperCase(),
      gender: data?.sex, // H / M
      email: data?.email,
      phone: data?.mobilePhone,
      prefix: data?.prefix
    }),
    documentDetails: new DocumentDetails({
      personType: data?.personType,
      documentType: data?.documentType,
      documentNumber: data?.documentNumber,
      documentExpirationDate: data?.documentExpirationDate,
      documentId: file?.documentId || file?.documentData,
      fileName: file?.fileName,
      mimeType: mimeType
    }),
    addressDetails: new AddressDetails({
      postalCode: data?.postalCode,
      country: data?.nationality,
      city: data?.city,
      province: data?.province,
      address: data?.street,
      streetType: data?.streetType,
      roadNumber: data?.house,
      addressExtra: data?.restAddress,
      addressNormalize: data?.addressNormalice,
      houseType: String(data?.houseType),
      gateway: data?.gateway,
      stair: data?.stair,
      floor: data?.floor,
      doorway: data?.doorway
    }),
    relationship: data?.relationship,
    hasHealthPolicy: data?.hasHealthPolicy,
    healthPolicyNumber: data?.healthPolicyNumber,

    // todo convert to dataProtection
    privacyPolicyConsent: dataProtection?.privacyPolicyConsent,
    declaration: data.policyHolderConsent,
    // asisaNewsConsent: dataProtection?.asisaNewsConsent ?? null, //? borrar??

    thirdPartyWrittenAdvertising: dataProtection?.thirdPartyTelephoneAdvertising,
    thirdPartyTelephoneAdvertising: dataProtection?.thirdPartyWrittenAdvertising,
    dataTransfer: dataProtection?.dataTransfer
  })
}

export default class {
  static fromResponse(
    data: DentalGetRecoveryResponse,
    product: DentalInsurance,
    relationships: DentalRelationshipType[]
  ): DentalHiringAnswers {
    const holder = _getHealthInsured(data.policyHolder, data.policyHolder.role, data.documents[0])
    if (!holder.userDetails.email) {
      holder.userDetails.email = data.hiring.recoveryMail ?? null
    }

    let isFirstOne = true
    let hasRelationship = 'yes'
    const defaultRelationship = relationships.find((r) => r.default && !r.disabled)
    const insureds = data.insureds.map((insured: any, index: number) => {
      const isHolder =
        data.policyHolder.role === UserRoleEnum.HOLDER_INSURED && insured.riskNumber === 1
      const document = data.documents[index]
      const role = isHolder ? UserRoleEnum.HOLDER_INSURED : UserRoleEnum.INSURED
      const isFirstInsured = !isHolder && isFirstOne
      if (isFirstInsured) {
        isFirstOne = false
      }

      if (hasRelationship === 'yes' && insured.relationship === defaultRelationship?.id) {
        hasRelationship = 'no'
      }

      const insuredData = _getHealthInsured({ ...insured, isFirstInsured }, role, document)

      return new DentalHiringInsured(insuredData)
    })

    if (holder.role === UserRoleEnum.HOLDER_INSURED) {
      const preContractualDocumentChecked = holder.preContractualDocumentChecked
      const thirdPartiesAnswered = holder.thirdPartiesAnswered
      const thirdPartyTelephoneAdvertising = holder.thirdPartyTelephoneAdvertising
      const thirdPartyWrittenAdvertising = holder.thirdPartyWrittenAdvertising
      const dataTransfer = holder.dataTransfer
      const privacyPolicyConsent = holder.privacyPolicyConsent

      insureds[0].updateAttributes({
        preContractualDocumentChecked,
        thirdPartiesAnswered,
        thirdPartyTelephoneAdvertising,
        thirdPartyWrittenAdvertising,
        dataTransfer,
        privacyPolicyConsent
      })
      insureds[0].userDetails.updateAttributes({
        email: holder.userDetails.email
      })
      insureds[0].healthPolicyNumber = data.insureds[0].healthPolicyNumber
      holder.updateAttributes(insureds[0])
    }

    return new DentalHiringAnswers({
      quotationId: data.quotationId,
      businessLine: data.hiring.businessLine,
      billingCycle: CommonMapper.fromDXPProductPrice(data.hiring.billingCycle),
      price: data.hiring.price,
      totalPrice: data.hiring.totalPrice,
      product: new Insurance({
        ...product,
        quotationNumber: String(data.hiring.quotationNumber),
        clientCode: String(data.hiring.clientCode),
        productCode: String(data.hiring.productCode)
      }),
      processStatus: new ProcessStatus({
        statusCode: data.hiring.statusCode,
        stepCode: data.hiring.stepCode
      }),
      startDate: CommonMapper.fromMiddlewareDate(data.hiring.insuranceStartDate),
      bankDetails: new BankDetails({
        name: data.paymentMeans?.holderName,
        iban: data.paymentMeans?.iban
      }),
      holder,
      insureds,
      hasRelationship
    })
  }

  static toRequest() {}
}
